import storeService from '../services/store';
import storageService from '../utils/localStorage';
import { notify } from './notificationReducer';
import getErrorMsg from '../utils/getErrorMsg';
import type { AppDispatch } from '../store';
import type { CosmeticsState, ReduxAction, StoreItem } from '../types/common';

/**
 * The store catalog and purchase status.
 *
 * Deliberately NOT part of economyReducer, which already carries config,
 * balances, the cap and the ledger.
 *
 * What is NOT here: equipped slots. Those live in `state.user.cosmetics.equipped`
 * and nowhere else — mirroring them into a second slice would create two
 * sources of truth that drift the moment one update path forgets the other.
 */
const initialState: CosmeticsState = {
  catalog: [],
  loaded: false,
  loading: false,
  error: null,
  purchasing: null,
};

const cosmeticsReducer = (
  state: CosmeticsState = initialState,
  action: ReduxAction<any>
): CosmeticsState => {
  switch (action.type) {
    case 'COSMETICS_CATALOG_START':
      return { ...state, loading: true, error: null };
    case 'COSMETICS_CATALOG_FAIL':
      return { ...state, loading: false, error: action.payload as string };
    case 'COSMETICS_SET_CATALOG':
      return {
        ...state,
        catalog: action.payload as StoreItem[],
        loaded: true,
        loading: false,
        error: null,
      };
    case 'COSMETICS_PURCHASE_START':
      return { ...state, purchasing: action.payload as string };
    case 'COSMETICS_PURCHASE_END':
      return { ...state, purchasing: null };
    /**
     * Mark the granted keys owned in place rather than re-fetching. A bundle
     * grants several keys at once, so the payload is a list.
     */
    case 'COSMETICS_MARK_OWNED': {
      const owned = new Set(action.payload as string[]);
      if (owned.size === 0) return state;
      return {
        ...state,
        catalog: state.catalog.map((item) =>
          owned.has(item.key) ? { ...item, owned: true, source: item.source ?? 'purchase' } : item
        ),
      };
    }
    /**
     * Keep the catalog's `equipped` flags in step with the user slice. The
     * equipped keys still live on the user; this is a derived display flag, so
     * it is recomputed from that one source instead of being set independently.
     */
    case 'COSMETICS_SYNC_EQUIPPED': {
      const equipped = new Set(
        Object.values((action.payload as Record<string, string | null>) || {}).filter(Boolean) as string[]
      );
      return {
        ...state,
        catalog: state.catalog.map((item) =>
          item.equipped === equipped.has(item.key) ? item : { ...item, equipped: equipped.has(item.key) }
        ),
      };
    }
    default:
      return state;
  }
};

export const fetchCatalog = () => {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: 'COSMETICS_CATALOG_START' });
    try {
      const catalog = await storeService.getCatalog();
      dispatch({ type: 'COSMETICS_SET_CATALOG', payload: catalog });
    } catch (err) {
      dispatch({ type: 'COSMETICS_CATALOG_FAIL', payload: getErrorMsg(err) });
    }
  };
};

/**
 * Buy an item.
 *
 * Double-tap protection is layered: the `purchasing` guard stops a second click
 * from ever leaving the device, and if a response is lost and the user retries
 * by hand, the server's unique {userId, itemKey} index refunds the loser. The
 * per-attempt idempotency key covers the third case — the same request arriving
 * twice — matching how awards work.
 */
export const purchaseItem = (itemKey: string, itemName: string) => {
  return async (dispatch: AppDispatch, getState: () => any) => {
    if (getState().cosmetics.purchasing) return null;

    const idempotencyKey = `${itemKey}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    dispatch({ type: 'COSMETICS_PURCHASE_START', payload: itemKey });
    try {
      const result = await storeService.purchase(itemKey, idempotencyKey);

      // Reuse the economy slice's existing freshness mechanism rather than
      // inventing a second one; the response already carries the new balance.
      dispatch({ type: 'ECONOMY_PATCH_BALANCES', payload: { coins: result.coins } });
      dispatch({ type: 'COSMETICS_MARK_OWNED', payload: result.granted ?? [] });

      // A reaction pack unlocks its reaction server-side, but the picker reads the
      // cached user (and localStorage, which survives reloads) — so patch both,
      // or the reaction stays locked until the next login.
      if (Array.isArray(result.unlockedReactions)) {
        const unlockedReactions = result.unlockedReactions;
        dispatch({ type: 'UPDATE_COSMETICS', payload: { unlockedReactions } });
        const stored = storageService.loadUser();
        if (stored) storageService.saveUser({ ...stored, unlockedReactions });
      }

      const alreadyOwned = (result.alreadyOwned ?? []).length;
      dispatch(
        notify(
          alreadyOwned > 0
            ? `${itemName} unlocked — you already had ${alreadyOwned} of these.`
            : `${itemName} is yours 🎉`,
          'success'
        )
      );
      return result;
    } catch (err) {
      dispatch(notify(getErrorMsg(err), 'error'));
      throw err;
    } finally {
      dispatch({ type: 'COSMETICS_PURCHASE_END' });
    }
  };
};

/**
 * Equip an item into its slot (or null to clear it). Optimistic, because the
 * whole point is that the preview reacts instantly; reverted on failure.
 */
export const equipCosmetic = (slot: string, itemKey: string | null) => {
  return async (dispatch: AppDispatch, getState: () => any) => {
    const previous = getState().user?.cosmetics?.equipped ?? {};
    const field = SLOT_FIELD[slot];
    if (!field) return;

    const optimistic = { ...previous, [field]: itemKey };
    applyEquipped(dispatch, optimistic);

    try {
      const result = await storeService.equip(slot, itemKey);
      // Trust the server's echo over the guess.
      applyEquipped(dispatch, result.equipped ?? optimistic);
    } catch (err) {
      applyEquipped(dispatch, previous);
      dispatch(notify(getErrorMsg(err), 'error'));
      throw err;
    }
  };
};

export const setSeasonalOptOut = (seasonalOptOut: boolean) => {
  return async (dispatch: AppDispatch, getState: () => any) => {
    await storeService.updatePreferences(seasonalOptOut);
    const cosmetics = { ...(getState().user?.cosmetics ?? {}), seasonalOptOut };
    dispatch({ type: 'UPDATE_COSMETICS', payload: { cosmetics } });
    const stored = storageService.loadUser();
    if (stored) storageService.saveUser({ ...stored, cosmetics });
  };
};

/** Slot name (wire) -> the field it occupies on the user. Mirrors the server's SLOT_FIELD. */
const SLOT_FIELD: Record<string, string> = {
  avatar_frame: 'avatarFrame',
  profile_effect: 'profileEffect',
  post_skin: 'postSkin',
  badge: 'badge',
};

/** Write equipped slots to the user slice, localStorage, and the catalog flags together. */
const applyEquipped = (dispatch: AppDispatch, equipped: Record<string, string | null>) => {
  const stored = storageService.loadUser();
  const cosmetics = { ...(stored?.cosmetics ?? {}), equipped };
  dispatch({ type: 'UPDATE_COSMETICS', payload: { cosmetics } });
  if (stored) storageService.saveUser({ ...stored, cosmetics });
  dispatch({ type: 'COSMETICS_SYNC_EQUIPPED', payload: equipped });
};

export default cosmeticsReducer;
